import { MapLabel } from "PosterEditor/model/poster-project";
import { MapElement, drawSelectedRect } from "../canvas-drawing";
import { coordinatesToPoints } from "../coordinate-utils";
import { rectWithPadding } from "../rect-utils";
import { drawRoundedRect } from "./canvas-drawing-helpers";
import {
  PointCoordinate,
  Size,
  Rect,
  LabelPointerSide,
  LabelSideHorizontal,
  LabelSideVertical,
  LabelDecoration,
  allowSidePointerForDecoration,
} from "PosterEditor/model/poster-types";
import { Point } from "PosterEditor/model/poster-types";
import { pointIsInsideRect, resizeRectFromCenter } from "../point-utils";
import { convertToRGBA } from "../color-utils";
import { drawDecoration } from "../label-decoration-drawing";
import { MapDrawingConfig } from "../canvas-drawing";
import { Shadow } from "PosterEditor/model/poster-types";

const applyShadow = (shadow: Shadow, ctx: any, scale: number) => {
  if (shadow.isHidden) {
    return;
  }
  ctx.save();
  ctx.shadowColor = convertToRGBA(shadow.color, shadow.opacity);
  ctx.shadowBlur = shadow.blur * scale; // Adjust based on scale
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = shadow.offset.height * scale;
};

const removeShadow = (shadow: Shadow | undefined, ctx: any) => {
  if (shadow?.isHidden) {
    return;
  }
  ctx.restore();
};

export const drawMapLabels = ({
  map,
  canvasElement,
  project,
  scale,
  pointerLocation,
  elementToHighlight,
  selectedElement,
  imageCache,
}: MapDrawingConfig) => {
  const ctx = canvasElement.current.getContext("2d");
  const labels = project.styledPoints
    .map((label) => label.label)
    .filter((label): label is MapLabel => label != null);
  const labelUnderCursor = drawLabels(
    labels,
    map,
    scale,
    ctx,
    pointerLocation,
    selectedElement?.mapLabel,
    elementToHighlight,
    imageCache
  );
  if (labelUnderCursor.labelUnderCursor) {
    const mapElement: MapElement = {
      type: "mapLabel",
      pointCoordinate: labelUnderCursor.labelUnderCursor.pointCoordinate,
      rect: labelUnderCursor.labelUnderCursor.rect,
      element: labelUnderCursor.labelUnderCursor.label,
    };
    return { elementUnderPointerLocation: mapElement };
  }
  return { elementUnderPointerLocation: undefined };
};

const drawLabels = (
  labels: MapLabel[],
  map: any,
  scale: number,
  ctx: CanvasRenderingContext2D,
  pointerLocation: Point | undefined,
  selectedLabel: MapLabel | undefined,
  elementToHighlight: MapElement | undefined,
  imageCache?: Map<string, HTMLImageElement>
): {
  labelUnderCursor:
    | { label: MapLabel; rect: Rect; pointCoordinate: PointCoordinate }
    | undefined;
} => {
  if (!labels) return { labelUnderCursor: undefined };
  const coordinates = labels.map((l) => l.coordinate);
  const points = coordinatesToPoints(map, scale, coordinates);

  let labelUnderCursor:
    | { label: MapLabel; rect: Rect; pointCoordinate: PointCoordinate }
    | undefined = undefined;
  points.forEach((point, index) => {
    const label = labels[index]; // Direct lookup
    const labelDrawn = drawLabel2(
      ctx,
      label,
      point,
      scale,
      pointerLocation,
      label.id === selectedLabel?.id,
      elementToHighlight,
      imageCache
    );
    if (labelDrawn && labelDrawn.isUnderCursor) {
      const pointCoordinate: PointCoordinate = {
        point: point,
        coordinate: {
          lat: label.coordinate.lat,
          lng: label.coordinate.lng,
        },
      };
      labelUnderCursor = { label, rect: labelDrawn.rect, pointCoordinate };
    }
  });
  return { labelUnderCursor };
};

const emptyLabelString = "label";

export const drawLabel2 = (
  ctx: CanvasRenderingContext2D,
  label: MapLabel,
  point: { x: number; y: number },
  scale: number,
  pointerLocation: Point | undefined,
  isSelected: boolean,
  elementToHighlight: MapElement | undefined,
  imageCache?: Map<string, HTMLImageElement>
): { rect: Rect; isUnderCursor: boolean } | undefined => {
  if (!label || !label.style) return undefined;
  ctx.save();
  const id = label.style.imageName ?? "placeholder";
  const imageElement = imageCache?.get(id ?? "");
  const willDrawImage = label.style.showImage && imageElement !== undefined;

  const hasVisibleContent = willDrawImage || label.style.showText;
  if (!hasVisibleContent) {
    ctx.restore();
    return undefined;
  }
  /////////////////////
  // Common variables
  /////////////////////
  const offset = {
    x: label.style.offset.x * scale,
    y: label.style.offset.y * scale,
  };
  const origin = { x: point.x + offset.x, y: point.y + offset.y };

  const scaledFontSize = label.style.fontSize * scale;
  const textSize = textSizeInContext(
    label.text,
    label.style.font,
    scaledFontSize,
    ctx,
    label.style.lineSpacing ?? 100
  );

  const imageAspectRatio = imageElement
    ? imageElement.width / imageElement?.height
    : 0;
  const imageHeight = label.style.imageSize * scale * 4;
  const imageWidth = imageHeight * imageAspectRatio;

  const borderWidth = label.style.borderWidth * 0.5 * scale;
  const marginWidth = borderWidth;

  const outerWidth =
    (willDrawImage ? imageWidth : textSize.width) + borderWidth * 2;

  let size: Size;

  if (willDrawImage) {
    const textMargin = label.style.showText ? marginWidth : 0;
    const textHeight = label.style.showText ? textSize.height : 0;
    const bottomBorderWidth = borderWidth; //label.style.showText ? marginWidth : borderWidth;
    size = {
      width: outerWidth,
      height:
        imageHeight + textMargin + textHeight + bottomBorderWidth + borderWidth,
    };
  } else {
    size = {
      width: outerWidth,
      height: textSize.height + borderWidth * 2,
    };
  }

  const outerRect: Rect = {
    x: origin.x,
    y: origin.y,
    width: size.width,
    height: size.height,
  };

  const textWidthBreaksBounds =
    textSize.width > outerRect.width && label.style.borderWidth > 0;

  const maxAllowedRadius = label.style.showText
    ? (textSize.height + borderWidth + marginWidth * 2) / 2
    : Math.min(size.width, size.height) / 2;
  const targetRadius = label.style.borderRadius * scale;
  const outerRadius = Math.max(0, Math.min(targetRadius, maxAllowedRadius));

  const opacity = label.style.backgroundOpacity ?? 1;

  /////////////////////
  // 📈 Draw line to label
  /////////////////////
  const allowCornerConnection = label.style.borderRadius === 0;
  const decoration: LabelDecoration = label.style.decorationStyle.decoration;
  const side = labelSide(
    outerRect,
    point,
    allowCornerConnection,
    allowSidePointerForDecoration(decoration) || willDrawImage
  );
  if (label.style.showPointer) {
    if (label.style.shadow) {
      applyShadow(label.style.shadow, ctx, scale);
    }
    if ((label.style.pointerThickness ?? 0) > 0) {
      ctx.strokeStyle = label.style.pointerColor ?? "black";
      ctx.lineWidth = (label.style.pointerThickness ?? 1) * scale;
      ctx.setLineDash([]);
      if (!willDrawImage) {
        drawLineToLabel(
          ctx,
          point,
          side,
          outerRect,
          (label.style.pointerThickness ?? 0) * scale
        );
      } else {
        drawLineToCenter(ctx, point, outerRect);
      }
    }
    removeShadow(label.style.shadow, ctx);
  }

  /////////////////////
  // ⬜️ Draw background
  /////////////////////

  if (willDrawImage || label.style.showText) {
    ctx.save();
    ctx.fillStyle = convertToRGBA(label.style.backgroundColor, opacity);

    const accountForText = label.style.showText && textWidthBreaksBounds;

    const corners = accountForText
      ? {
          topLeft: true,
          topRight: true,
          bottomRight: false,
          bottomLeft: false,
        }
      : undefined;

    const backgroundHeight = accountForText
      ? size.height - textSize.height - borderWidth - marginWidth
      : size.height;

    ctx.save();

    //We need to erase the previous drawing under our image background (the pointer) and re-draw the background with any opacity..
    ctx.globalCompositeOperation = "destination-out"; // This erases previous drawings
    ctx.fillStyle = "rgba(0,0,0,1)"; // Fully opaque
    drawRoundedRect(
      ctx,
      origin.x,
      origin.y,
      size.width,
      backgroundHeight,
      outerRadius,
      corners
    );
    ctx.fill();
    ctx.restore();

    if (label.style.shadow) {
      applyShadow(label.style.shadow, ctx, scale);
    }
    // Actual drawing with selected opacity.
    ctx.save();
    ctx.fillStyle = convertToRGBA(label.style.backgroundColor, opacity);
    drawRoundedRect(
      ctx,
      origin.x,
      origin.y,
      size.width,
      backgroundHeight,
      outerRadius,
      corners
    );
    ctx.fill();
    ctx.restore();
  }
  removeShadow(label.style.shadow, ctx);

  /////////////////////
  // 🏞️ Draw photo
  /////////////////////
  const photoRect = {
    x: outerRect.x + borderWidth,
    y: outerRect.y + borderWidth,
    width: imageWidth,
    height: imageHeight,
  };
  ctx.save();
  if (willDrawImage) {
    const imageRadius = outerRadius > 0 ? outerRadius - borderWidth / 2 : 0;

    if (borderWidth === 0 && label.style.shadow) {
      applyShadow(label.style.shadow, ctx, scale);
      drawRoundedRect(
        ctx,
        photoRect.x,
        photoRect.y,
        photoRect.width,
        photoRect.height,
        imageRadius
      );
      ctx.fillStyle = "rgba(0, 0, 0, 0)";
      ctx.fill();
      removeShadow(label.style.shadow, ctx);
    }

    drawRoundedRect(
      ctx,
      photoRect.x,
      photoRect.y,
      photoRect.width,
      photoRect.height,
      imageRadius
    );
    ctx.clip();

    if (imageElement) {
      ctx.drawImage(
        imageElement,
        photoRect.x,
        photoRect.y,
        photoRect.width,
        photoRect.height
      );
    }
    ctx.restore();
  }

  /////////////////////
  // 🅰 Draw text
  /////////////////////
  if (label.style.showText) {
    // textX will be calculated per-line based on alignment
    const textY = willDrawImage
      ? outerRect.y + borderWidth + imageHeight + marginWidth
      : outerRect.y + borderWidth;

    /////////////////////
    // Text background
    /////////////////////

    if (label.style.shadow) {
      applyShadow(label.style.shadow, ctx, scale);
    }
    if (textWidthBreaksBounds) {
      // Use base line height for border calculation to avoid wider padding with more lines
      const baseLineHeight =
        textSize.ascent + textSize.descent || scaledFontSize * 1.2;
      const minimumBorder =
        label.style.borderWidth > 0 ? baseLineHeight * 0.2 : 0;
      const border = Math.max(minimumBorder, borderWidth);

      // When text breaks bounds, always center the background block
      const textBackgroundX =
        outerRect.x + (outerRect.width - textSize.width) / 2 - border;

      //Make the height flush with the outer rect bottom.
      const y = photoRect.y + photoRect.height;
      const height = outerRect.y + size.height - y;
      const textBackgroundRect = {
        x: textBackgroundX,
        y: y,
        width: textSize.width + border * 2,
        height: height,
      };
      ctx.fillStyle = convertToRGBA(
        label.style.backgroundColor,
        label.style.backgroundOpacity ?? 1
      );

      const textBackgroundRadius = Math.min(
        textBackgroundRect.height / 2,
        outerRadius
      );

      // Erase the background under the text background rectangle
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,1)";
      drawRoundedRect(
        ctx,
        textBackgroundRect.x,
        textBackgroundRect.y,
        textBackgroundRect.width,
        textBackgroundRect.height,
        textBackgroundRadius
      );
      ctx.fill();
      ctx.restore();

      // Draw the semi-transparent background
      ctx.save();
      ctx.fillStyle = convertToRGBA(
        label.style.backgroundColor,
        label.style.backgroundOpacity ?? 1
      );
      drawRoundedRect(
        ctx,
        textBackgroundRect.x,
        textBackgroundRect.y,
        textBackgroundRect.width,
        textBackgroundRect.height,
        textBackgroundRadius
      );
      ctx.fill();
      ctx.restore();
    }

    /////////////////////
    // Text
    /////////////////////

    const verticalAdjustment = 0; //textSize.height * 0.1; // Text seems to be slightly too high.

    // ⬇️ IMPORTANT: clear any residual shadow before drawing decoration and text
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    ctx.save();
    if (!willDrawImage) {
      ctx.setLineDash([]);
      drawDecoration(label.style.decorationStyle, ctx, outerRect, side, scale);
    }

    ctx.fillStyle = label.style.textColor ?? "black";
    ctx.font = `${scaledFontSize}px ${label.style.font}`;
    ctx.textBaseline = "middle";

    // Draw multiline text
    const lines = label.text.split("\n");
    const baseLineHeight =
      textSize.ascent + textSize.descent || scaledFontSize * 1.2;

    // Calculate line height multiplier from lineSpacingSetting (50-200)
    // 50 → 0.8x (very tight, overlap), 100 → 1.2x (default), 200 → 2.0x (very loose)
    const lineSpacingSetting = label.style.lineSpacing ?? 100;
    const lineHeightMultiplier = 0.8 + (lineSpacingSetting - 50) / 125;
    const singleLineHeight = baseLineHeight * lineHeightMultiplier;

    const alignment = label.style.alignment || "center";

    // When text breaks bounds, center the overall text block
    const textBlockX = textWidthBreaksBounds
      ? outerRect.x + (outerRect.width - textSize.width) / 2
      : outerRect.x + borderWidth;
    const textBlockWidth = textWidthBreaksBounds
      ? textSize.width
      : outerRect.width - borderWidth * 2;

    lines.forEach((line, index) => {
      // Calculate X position for each line based on alignment
      const lineWidth = ctx.measureText(line).width;
      let lineX: number;

      if (alignment === "left") {
        lineX = textBlockX;
      } else if (alignment === "right") {
        lineX = textBlockX + textBlockWidth - lineWidth;
      } else {
        // center
        lineX = textBlockX + (textBlockWidth - lineWidth) / 2;
      }

      const lineY =
        textY + textSize.ascent + verticalAdjustment + singleLineHeight * index;
      ctx.fillText(line, lineX, lineY);
    });
    ctx.restore();
  }

  /////////////////////
  // 🖱️ Pointer / selection handling.
  /////////////////////
  let isHighlighted = false;
  if (pointerLocation) {
    if (pointIsInsideRect(pointerLocation, outerRect)) {
      isHighlighted = true;
    }
  }
  ctx.restore();
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  if (isSelected) {
    // When text breaks bounds, use the wider text rect for selection
    let selectionRect = outerRect;
    if (textWidthBreaksBounds) {
      const baseLineHeight = textSize.ascent + textSize.descent || scaledFontSize * 1.2;
      const minimumBorder = label.style.borderWidth > 0 ? baseLineHeight * 0.2 : 0;
      const border = Math.max(minimumBorder, borderWidth);

      const textBackgroundX = outerRect.x + (outerRect.width - textSize.width) / 2 - border;
      const textBackgroundWidth = textSize.width + border * 2;

      selectionRect = {
        x: textBackgroundX,
        y: outerRect.y,
        width: textBackgroundWidth,
        height: outerRect.height,
      };
    }

    const paddedRect = rectWithPadding(selectionRect, 10);
    drawSelectedRect(paddedRect, ctx);
  }

  ctx.restore();
  return { rect: outerRect, isUnderCursor: isHighlighted };
};

const drawLineToLabel = (
  ctx: CanvasRenderingContext2D,
  rootPoint: Point,
  side: LabelPointerSide,
  labelRect: Rect,
  decorationWidth: number
) => {
  const labelPoint = labelPointFromSide(side, labelRect, decorationWidth);
  ctx.beginPath();
  ctx.moveTo(rootPoint.x, rootPoint.y);
  ctx.lineTo(labelPoint.x, labelPoint.y);
  ctx.stroke();
};

const drawLineToCenter = (
  ctx: CanvasRenderingContext2D,
  rootPoint: Point,
  labelRect: Rect
) => {
  const labelCenter = {
    x: labelRect.x + labelRect.width / 2,
    y: labelRect.y + labelRect.height / 2,
  };
  ctx.beginPath();
  ctx.moveTo(rootPoint.x, rootPoint.y);
  ctx.lineTo(labelCenter.x, labelCenter.y);
  ctx.stroke();
};

const labelPointFromSide = (
  labelSide: LabelPointerSide,
  labelRect: Rect,
  decorationWidth: number
): { x: number; y: number } => {
  let horizontal: LabelSideHorizontal;
  let vertical: LabelSideVertical;

  const padding = decorationWidth;
  const resizedRect = resizeRectFromCenter(labelRect, padding);

  if (labelSide.horizontal === "center") {
    horizontal = "center";
  } else if (labelSide.horizontal === "left") {
    horizontal = "left";
  } else {
    horizontal = "right";
  }

  if (labelSide.vertical === "center") {
    vertical = "center";
  } else if (labelSide.vertical === "top") {
    vertical = "top";
  } else {
    vertical = "bottom";
  }

  // create point from resized rect with x, y label sides
  let x = 0;
  let y = 0;
  if (horizontal === "center") {
    x = resizedRect.x + resizedRect.width / 2;
  } else if (horizontal === "left") {
    x = resizedRect.x;
  } else {
    x = resizedRect.x + resizedRect.width;
  }

  if (vertical === "center") {
    y = resizedRect.y + resizedRect.height / 2;
  } else if (vertical === "top") {
    y = resizedRect.y;
  } else {
    y = resizedRect.y + resizedRect.height;
  }
  return { x, y };
};

// UTILITY

const labelSide = (
  labelRect: Rect,
  targetPoint: Point,
  allowCorners: boolean,
  allowSides: boolean
): LabelPointerSide => {
  const labelMaxX = labelRect.x + labelRect.width;
  const labelMaxY = labelRect.y + labelRect.height;
  let horizontal: LabelSideHorizontal;
  if (allowSides && labelRect.x < targetPoint.x && labelMaxX > targetPoint.x) {
    horizontal = "center";
  } else if (labelRect.x + labelRect.width / 2 < targetPoint.x) {
    horizontal = "right";
  } else {
    horizontal = "left";
  }

  let vertical: LabelSideVertical;
  if (allowSides && labelRect.y < targetPoint.y && labelMaxY > targetPoint.y) {
    vertical = "center";
  } else if (labelRect.y + labelRect.height / 2 < targetPoint.y) {
    if (!allowCorners && allowSides && horizontal !== "center") {
      vertical = "center";
    } else {
      vertical = "bottom";
    }
  } else {
    if (!allowCorners && allowSides && horizontal !== "center") {
      vertical = "center";
    } else {
      vertical = "top";
    }
  }

  return { horizontal, vertical };
};

export const labelRectInCanvas = (
  label: MapLabel,
  ctx: CanvasRenderingContext2D,
  map: any
): Rect => {
  let size = labelSizeInContext(label, ctx);
  const point = map.project([label.coordinate.lng, label.coordinate.lat]);
  const padding = label.style.padding ?? 0;
  const offset = label.style.offset ?? { x: 0, y: 0 };
  const x = point.x - padding + offset.x;
  const y = point.y - padding + offset.y;
  return { x, y, width: size.width, height: size.height };
};

export const labelSizeInContext = (
  label: MapLabel,
  ctx: CanvasRenderingContext2D
): { width: number; height: number } => {
  const fontSize = label.style.fontSize;
  const font = label.style.font;
  const padding = label.style.padding ?? 0;

  // Use textSizeInContext to handle multiline text
  const textSize = textSizeInContext(
    label.text,
    font,
    fontSize,
    ctx,
    label.style.lineSpacing ?? 100
  );

  const labelWidth = textSize.width + padding * 2;
  const labelHeight = textSize.height + padding * 2;

  return { width: labelWidth, height: labelHeight };
};

export const textSizeInContext = (
  text: string,
  font: string,
  fontSize: number,
  ctx: CanvasRenderingContext2D,
  lineSpacingSetting: number = 100
) => {
  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  // Split text by newlines to handle multiline text
  const lines = text.split("\n");

  // Measure each line and find the maximum width
  let maxWidth = 0;
  let totalAscent = 0;
  let totalDescent = 0;

  lines.forEach((line, index) => {
    const textMetrics = ctx.measureText(line);
    maxWidth = Math.max(maxWidth, textMetrics.width);

    // Only use the first line's metrics for ascent/descent calculations
    if (index === 0) {
      if (
        textMetrics.actualBoundingBoxAscent !== undefined &&
        textMetrics.actualBoundingBoxDescent !== undefined
      ) {
        totalAscent = textMetrics.actualBoundingBoxAscent;
        totalDescent = textMetrics.actualBoundingBoxDescent;
      } else {
        totalAscent = fontSize * 0.8;
        totalDescent = fontSize * 0.2;
      }
    }
  });

  // Calculate line height from the first line's metrics
  const baseLineHeight = totalAscent + totalDescent || fontSize * 1.2;

  // Calculate line height multiplier from lineSpacingSetting (50-200)
  // 50 → 0.8x (very tight, overlap), 100 → 1.2x (default), 200 → 2.0x (very loose)
  const lineHeightMultiplier = 0.8 + (lineSpacingSetting - 50) / 125;
  const singleLineHeight = baseLineHeight * lineHeightMultiplier;
  const lineSpacing = singleLineHeight - baseLineHeight;

  // Total height: base height for all lines + spacing between lines (not after last line)
  const textHeight =
    baseLineHeight * lines.length + lineSpacing * (lines.length - 1);

  return {
    width: maxWidth,
    height: textHeight,
    ascent: totalAscent,
    descent: totalDescent,
  };
};
